import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const TransactionList = () => {
  const server_url = localStorage.getItem("server_url");
  const company_id = localStorage.getItem("company_id") || "2";
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const grage_id = userData.grage_id || "1";
  const navigate = useNavigate();

  // Calculate default date range: current month
  const getDefaultDateRange = () => {
    const today = new Date();
    const fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
    const toDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    return {
      from_date: fromDate.toISOString().split("T")[0],
      to_date: toDate.toISOString().split("T")[0],
    };
  };

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    transaction: null,
  });

  // Date filters
  const [dateFilters, setDateFilters] = useState(getDefaultDateRange());
  const [isDefaultRange, setIsDefaultRange] = useState(true);

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    if (searchText.trim() == "") {
      setFilteredTransactions(transactions);
    } else {
      const filtered = transactions.filter(
        (transaction) =>
          (transaction.remark || "")
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          (transaction.expenses_categories_title || "")
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          (transaction.amount || "").toString().includes(searchText)
      );
      setFilteredTransactions(filtered);
    }
  }, [searchText, transactions]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("company_id", company_id);
      formData.append("transaction_grage_id", grage_id);
      formData.append("action", "view");

      if (dateFilters.from_date && dateFilters.to_date) {
        formData.append("start_date", dateFilters.from_date);
        formData.append("end_date", dateFilters.to_date);
      }

      if (searchText.trim() !== "") {
        formData.append("search", searchText);
      }

      const response = await axios.post(
        `${server_url}other_transaction.php`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.status == "1") {
        setTransactions(response.data.result || []);
        setFilteredTransactions(response.data.result || []);
      } else if (response.data.status == "3") {
        toast.error("Please login first");
        navigate("/login");
      } else if (response.data.status == "0") {
        setTransactions([]);
        setFilteredTransactions([]);
        // Don't show error toast for no data
        if (response.data.error !== "No transactions found") {
          toast.error(response.data.error || "Failed to fetch transactions");
        }
      } else {
        toast.error(response.data.error || "Failed to fetch transactions");
        setTransactions([]);
        setFilteredTransactions([]);
      }
    } catch (error) {
      console.error("Fetch transactions error:", error);
      toast.error("Unable to fetch transactions. Please try again.");
      setTransactions([]);
      setFilteredTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    fetchTransactions();
  };

  const handleDateFilterChange = (e) => {
    const { name, value } = e.target;
    setDateFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setIsDefaultRange(false);
  };

  const handleResetToDefault = () => {
    const defaultRange = getDefaultDateRange();
    setDateFilters(defaultRange);
    setIsDefaultRange(true);
    setTimeout(() => {
      fetchTransactions();
    }, 100);
  };

  const handleAddTransaction = () => {
    navigate("/add-transaction");
  };

  const handleEditTransaction = (transaction) => {
    navigate("/edit-transaction", { state: { transaction } });
  };

  const openDeleteModal = (transaction) => {
    setDeleteModal({ isOpen: true, transaction });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, transaction: null });
  };

  const confirmDeleteTransaction = async () => {
    const transaction = deleteModal.transaction;
    if (!transaction) return;

    setDeleteLoading(transaction.transaction_id);
    try {
      const formData = new FormData();
      formData.append("company_id", company_id);
      formData.append("transaction_id", transaction.transaction_id);
      formData.append("action", "delete");

      const response = await axios.post(
        `${server_url}other_transaction.php`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.status == "1") {
        toast.success("Transaction deleted successfully!");
        setTransactions((prev) =>
          prev.filter((i) => i.transaction_id !== transaction.transaction_id)
        );
        setFilteredTransactions((prev) =>
          prev.filter((i) => i.transaction_id !== transaction.transaction_id)
        );
        closeDeleteModal();
      } else {
        toast.error(response.data.error || "Failed to delete transaction");
      }
    } catch (error) {
      console.error("Delete transaction error:", error);
      toast.error("Unable to delete transaction. Please try again.");
    } finally {
      setDeleteLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  const formatAmount = (amount) => {
    return parseFloat(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getTransactionIcon = (type) => {
    return type == "in"
      ? { icon: "fas fa-arrow-down", color: "#10b981", bgColor: "#d1fae5" }
      : { icon: "fas fa-arrow-up", color: "#dc2626", bgColor: "#fee2e2" };
  };

  const calculateTotals = () => {
    const totals = filteredTransactions.reduce(
      (acc, transaction) => {
        if (transaction.transaction_type == "1") {
          acc.income += parseFloat(transaction.amount || 0);
        } else {
          acc.expense += parseFloat(transaction.amount || 0);
        }
        return acc;
      },
      { income: 0, expense: 0 }
    );

    totals.balance = totals.income - totals.expense;
    return totals;
  };

  const totals = calculateTotals();

  return (
    <div className="history-container">
      {/* Sticky Header Section - Same as InsuranceList */}
      <div
        className="sticky-filter"
        style={{ position: "fixed", top: "56px", width: "100%" }}
      >
        <div className="home-icon">
          <div className="section-home">
            {/* Date Filter Form - Same as InsuranceList */}
            <form onSubmit={handleSearch} className="search-form">
              <div className="input-group same-line">
                <input
                  type="date"
                  name="from_date"
                  value={dateFilters.from_date}
                  onChange={handleDateFilterChange}
                  className="search-input"
                  placeholder="From Date"
                />
                <span className="date-separator">to</span>
                <input
                  type="date"
                  name="to_date"
                  value={dateFilters.to_date}
                  onChange={handleDateFilterChange}
                  className="search-input"
                  placeholder="To Date"
                />
                <button type="submit" className="search-btn">
                  <i className="fas fa-search"></i>
                </button>
              </div>
            </form>

            {/* Search Bar Below Date Filters - Same as InsuranceList */}
            <div style={{ marginTop: "10px" }}>
              <div className="input-group same-line">
                <input
                  type="text"
                  placeholder="Search by remark or category..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyPress={(e) => e.key == "Enter" && handleSearch()}
                  className="search-input"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div
        className="content-area"
        style={{ paddingTop: isDefaultRange ? "130px" : "120px" }}
      >
        {/* Loading State */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 20px" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "3px solid #f3f3f3",
                borderTop: "3px solid #0352a1",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 15px",
              }}
            ></div>
            <div style={{ color: "#666" }}>Loading transactions...</div>
          </div>
        ) : (
          <>
            {/* Summary Cards - Only show when there are transactions */}
            {filteredTransactions.length > 0 && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "10px",
                  padding: "0 10px",
                  marginBottom: "15px",
                }}
              >
                <div
                  style={{
                    background: "#d1fae5",
                    padding: "12px",
                    borderRadius: "8px",
                    textAlign: "center",
                    border: "1px solid #10b981",
                    boxShadow: "0 2px 4px rgba(16, 185, 129, 0.1)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#065f46",
                      fontWeight: "600",
                      marginBottom: "5px",
                    }}
                  >
                    <i
                      className="fas fa-arrow-down"
                      style={{ marginRight: "5px" }}
                    ></i>
                    INCOME
                  </div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      color: "#065f46",
                    }}
                  >
                    ₹{formatAmount(totals.income)}
                  </div>
                </div>

                <div
                  style={{
                    background: "#fee2e2",
                    padding: "12px",
                    borderRadius: "8px",
                    textAlign: "center",
                    border: "1px solid #dc2626",
                    boxShadow: "0 2px 4px rgba(220, 38, 38, 0.1)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#991b1b",
                      fontWeight: "600",
                      marginBottom: "5px",
                    }}
                  >
                    <i
                      className="fas fa-arrow-up"
                      style={{ marginRight: "5px" }}
                    ></i>
                    EXPENSE
                  </div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      color: "#991b1b",
                    }}
                  >
                    ₹{formatAmount(totals.expense)}
                  </div>
                </div>
              </div>
            )}

            {/* Transaction List */}
            <div className="home-vehicles">
              {filteredTransactions.length > 0 ? (
                <div className="vehicles-list home-icon">
                  {filteredTransactions.map((transaction) => {
                    const iconInfo = getTransactionIcon(
                      transaction.transaction_type
                    );
                    const formattedDate = formatDate(
                      transaction.transaction_date
                    );
                    const createdDate = new Date(transaction.c_date);
                    const createdDateStr =
                      createdDate.toLocaleDateString("en-IN");
                    const createdTimeStr = createdDate.toLocaleTimeString(
                      "en-IN",
                      {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      }
                    );

                    return (
                      <div
                        key={transaction.transaction_id}
                        className="vehicle-card section-home"
                        style={{
                          borderLeft: `4px solid ${iconInfo.color}`,
                          cursor: "pointer",
                        }}
                        onClick={() => handleEditTransaction(transaction)}
                      >
                        <div className="vehicle-header">
                          {transaction.transaction_type == "1" ? (
                            <table style={{ width: "100%", textAlign: "left" }}>
                              <tbody>
                                {/* Remark */}
                                <tr>
                                  <td
                                    colSpan="2"
                                    style={{
                                      fontSize: "13px",
                                      color: "#666",
                                      padding: "2px 0",
                                    }}
                                  >
                                    <i
                                      className="fas fa-sticky-note"
                                      style={{
                                        marginRight: "8px",
                                        color: "#666",
                                      }}
                                    ></i>
                                    {transaction.remark || "No remark"}
                                  </td>
                                </tr>

                                {/* Transaction Date and Created Time */}
                                <tr>
                                  <td
                                    style={{
                                      fontSize: "13px",
                                      color: "#666",
                                      padding: "2px 0",
                                    }}
                                  >
                                    <i
                                      className="far fa-calendar"
                                      style={{
                                        marginRight: "8px",
                                        color: "#0352a1",
                                      }}
                                    ></i>
                                    {formattedDate}
                                  </td>
                                  <td
                                    style={{
                                      textAlign: "right",
                                      fontSize: "18px",
                                      fontWeight: "bold",
                                      color: iconInfo.color,
                                      paddingTop: "8px",
                                    }}
                                  >
                                    {transaction.transaction_type == "1"
                                      ? "+"
                                      : "-"}
                                    ₹{formatAmount(transaction.amount)}
                                  </td>
                                </tr>

                                {/* Created Date */}
                                <tr>
                                  <td
                                    style={{
                                      fontSize: "12px",
                                      color: "#999",
                                      padding: "8px 0 4px 0",
                                      fontStyle: "italic",
                                      borderTop: "1px solid #e2e8f0",
                                      marginTop: "8px",
                                      paddingTop: "8px",
                                    }}
                                  >
                                    <i
                                      className="fas fa-history"
                                      style={{ marginRight: "8px" }}
                                    ></i>
                                    Created: {createdDateStr}
                                  </td>
                                  <td
                                    style={{
                                      fontSize: "12px",
                                      color: "#999",
                                      padding: "8px 0 4px 0",
                                      fontStyle: "italic",
                                      borderTop: "1px solid #e2e8f0",
                                      marginTop: "8px",
                                      paddingTop: "8px",
                                    }}
                                  >
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openDeleteModal(transaction);
                                      }}
                                      disabled={
                                        deleteLoading ==
                                        transaction.transaction_id
                                      }
                                      style={{
                                        border: "none",
                                        cursor: "pointer",
                                        fontSize: "19px",
                                        fontWeight: "500",
                                        backgroundColor: "transparent",
                                        color: "#DB4437",
                                        float: "right",
                                      }}
                                      title="Delete Transaction"
                                    >
                                      {deleteLoading ==
                                      transaction.transaction_id ? (
                                        <i className="fas fa-spinner fa-spin"></i>
                                      ) : (
                                        <i className="fas fa-trash"></i>
                                      )}
                                    </button>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          ) : (
                            <table style={{ width: "100%", textAlign: "left" }}>
                              <tbody>
                                <tr>
                                  <td
                                   colSpan="2"
                                    style={{
                                      fontSize: "14px",
                                      color: "#2c3e50",
                                      padding: "4px 0",
                                    }}
                                  >
                                    {transaction.expenses_categories_title && (
                                      <>
                                        <i
                                          className="fas fa-tag"
                                          style={{
                                            marginRight: "8px",
                                            color: "#666",
                                          }}
                                        ></i>
                                        {transaction.expenses_categories_title}
                                      </>
                                    )}
                                  </td>
                                 
                                </tr>

                                {/* Remark */}
                                <tr>
                                  <td
                                    colSpan="2"
                                    style={{
                                      fontSize: "13px",
                                      color: "#666",
                                      padding: "2px 0",
                                    }}
                                  >
                                    <i
                                      className="fas fa-sticky-note"
                                      style={{
                                        marginRight: "8px",
                                        color: "#666",
                                      }}
                                    ></i>
                                    {transaction.remark || "No remark"}
                                  </td>
                                </tr>

                                {/* Transaction Date and Created Time */}
                                <tr>
                                  <td
                                   style={{
                                      fontSize: "13px",
                                      color: "#666",
                                      padding: "2px 0",
                                    }}
                                  >
                                    <i
                                      className="far fa-calendar"
                                      style={{
                                        marginRight: "8px",
                                        color: "#0352a1",
                                      }}
                                    ></i>
                                    {formattedDate}
                                  </td>
                                   <td
                                    style={{
                                      textAlign: "right",
                                      fontSize: "18px",
                                      fontWeight: "bold",
                                      color: iconInfo.color,
                                      paddingTop: "8px",
                                    }}
                                  >
                                    {transaction.transaction_type == "1"
                                      ? "+"
                                      : "-"}
                                    ₹{formatAmount(transaction.amount)}
                                  </td>
                                </tr>

                                {/* Created Date */}
                                <tr>
                                  <td
                                    style={{
                                      fontSize: "12px",
                                      color: "#999",
                                      padding: "8px 0 4px 0",
                                      fontStyle: "italic",
                                      borderTop: "1px solid #e2e8f0",
                                      marginTop: "8px",
                                      paddingTop: "8px",
                                    }}
                                  >
                                    <i
                                      className="fas fa-history"
                                      style={{ marginRight: "8px" }}
                                    ></i>
                                    Created: {createdDateStr}
                                  </td>
                                  <td
                                    style={{
                                      fontSize: "12px",
                                      color: "#999",
                                      padding: "8px 0 4px 0",
                                      fontStyle: "italic",
                                      borderTop: "1px solid #e2e8f0",
                                      marginTop: "8px",
                                      paddingTop: "8px",
                                    }}
                                  >
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openDeleteModal(transaction);
                                      }}
                                      disabled={
                                        deleteLoading ==
                                        transaction.transaction_id
                                      }
                                      style={{
                                        border: "none",
                                        cursor: "pointer",
                                        fontSize: "19px",
                                        fontWeight: "500",
                                        backgroundColor: "transparent",
                                        color: "#DB4437",
                                        float: "right",
                                      }}
                                      title="Delete Transaction"
                                    >
                                      {deleteLoading ==
                                      transaction.transaction_id ? (
                                        <i className="fas fa-spinner fa-spin"></i>
                                      ) : (
                                        <i className="fas fa-trash"></i>
                                      )}
                                    </button>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                !loading && (
                  <div className="no-vehicles section-home">
                    <div
                      className="no-vehicles-icon"
                      style={{ fontSize: "60px", color: "#0352a1" }}
                    >
                      <i className="fas fa-file-invoice-dollar"></i>
                    </div>
                    <div className="no-vehicles-title">
                      No Transactions Found
                    </div>
                    <div className="no-vehicles-text">
                      {searchText
                        ? "Try adjusting your search terms"
                        : `No transactions found for the selected date range (${formatDate(
                            dateFilters.from_date
                          )} to ${formatDate(dateFilters.to_date)})`}
                    </div>
                  </div>
                )
              )}
            </div>
          </>
        )}
      </div>

      {/* Floating Add Button - Always show */}
      <button
        className="floating-add-btn"
        onClick={handleAddTransaction}
        title="Add New Transaction"
      >
        <i className="fas fa-plus"></i>
      </button>

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              width: "100%",
              maxWidth: "350px",
              padding: "24px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: "60px",
                height: "60px",
                margin: "0 auto 20px",
                backgroundColor: "#fee2e2",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i
                className="fas fa-exclamation-triangle"
                style={{ fontSize: "24px", color: "#dc2626" }}
              ></i>
            </div>

            <h3
              style={{
                margin: "0 0 12px 0",
                fontSize: "18px",
                fontWeight: "600",
                color: "#1f2937",
              }}
            >
              Delete Transaction
            </h3>
            <p
              style={{
                margin: "0 0 24px 0",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              Are you sure you want to delete this transaction? This action
              cannot be undone.
            </p>

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={closeDeleteModal}
                disabled={deleteLoading !== null}
                style={{
                  flex: 1,
                  padding: "12px",
                  border: "2px solid #e2e8f0",
                  background: "white",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteTransaction}
                disabled={deleteLoading !== null}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "#dc2626",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {deleteLoading == deleteModal.transaction?.transaction_id ? (
                  <>
                    <i
                      className="fas fa-spinner fa-spin"
                      style={{ marginRight: "5px" }}
                    ></i>
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .history-container {
          min-height: 100vh;
          background: white;
          padding: 0;
          position: relative;
        }

        .sticky-filter {
          position: sticky;
          top: 0;
          z-index: 100;
          background: white;
          padding: 8px 0;
          border-bottom: 1px solid #e2e8f0;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .content-area {
          padding-top: 120px;
          padding-bottom: 80px;
          transition: padding-top 0.3s ease;
        }

        .home-icon .section-home {
          padding: 8px 12px 12px 12px;
        }
        .home-icon {
          padding-left: 0px;
          padding-right: 0px;
        }

        .input-group.same-line {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .search-input {
          flex: 1;
          padding: 10px 12px;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          font-size: 14px;
          background: #f8f9fa;
        }

        .date-separator {
          color: #718096;
          font-size: 14px;
          font-weight: 500;
          white-space: nowrap;
        }

        .search-btn {
          background: #0352a1;
          color: white;
          border: none;
          border-radius: 12px;
          padding: 10px 12px;
          min-width: 40px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .default-indicator {
          margin-top: 8px;
          text-align: center;
        }

        .default-badge {
          background: #e3f2fd;
          color: #0352a1;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .default-badge i {
          font-size: 14px;
        }

        .home-vehicles {
          padding: 20px 10px;
        }

        .vehicles-list {
          display: grid;
          gap: 15px;
        }

        .vehicle-card {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 16px;
          border-left: 4px solid #0352a1;
          transition: all 0.3s ease;
        }

        .vehicle-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .vehicle-header {
          width: 100%;
        }

        .vehicle-header table {
          width: 100%;
          border-collapse: collapse;
        }

        .vehicle-header td {
          padding: 4px 0;
          vertical-align: top;
        }

        .no-vehicles {
          text-align: center;
          padding: 40px 20px;
          background: #f8f9fa;
          border-radius: 12px;
          margin: 20px 0;
        }

        .no-vehicles-icon {
          font-size: 48px;
          margin-bottom: 15px;
        }

        .no-vehicles-title {
          font-size: 18px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 8px;
        }

        .no-vehicles-text {
          color: #666;
          margin-bottom: 20px;
          line-height: 1.4;
          max-width: 300px;
          margin-left: auto;
          margin-right: auto;
        }

        .reset-default-btn {
          background: #0352a1;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 10px 20px;
          font-size: 14px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin: 0 5px;
        }

        .reset-default-btn:hover {
          background: #023e7d;
        }

        .floating-add-btn {
          position: fixed;
          bottom: 80px;
          right: 20px;
          width: 60px;
          height: 60px;
          background: #0352a1;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(3, 82, 161, 0.3);
          z-index: 99;
          transition: all 0.3s ease;
          border: none;
        }

        .floating-add-btn:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 16px rgba(3, 82, 161, 0.4);
        }

        .floating-add-btn:active {
          transform: scale(0.95);
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 768px) {
          .content-area {
            padding-top: 140px;
          }

          .home-vehicles {
            padding: 0px 8px;
          }

          .vehicle-card {
            padding: 12px;
          }

          .vehicle-header td {
            font-size: 14px;
          }

          .sticky-filter {
            padding: 6px 0;
          }

          .input-group.same-line {
            flex-wrap: wrap;
          }

          .search-input {
            min-width: 120px;
          }

          .date-separator {
            display: none;
          }

          .floating-add-btn {
            bottom: 70px;
            right: 15px;
            width: 56px;
            height: 56px;
            font-size: 22px;
          }
        }

        @media (max-width: 480px) {
          .content-area {
            padding-top: 110px;
          }

          .sticky-filter {
            padding: 4px 0;
          }

          .input-group.same-line {
            gap: 6px;
          }

          .search-input {
            font-size: 13px;
            padding: 8px 10px;
          }

          .search-btn {
            padding: 8px 10px;
            min-width: 36px;
          }

          .floating-add-btn {
            bottom: 60px;
            right: 12px;
            width: 52px;
            height: 52px;
            font-size: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default TransactionList;
